import { yupResolver } from '@hookform/resolvers/yup';
import { FC, Fragment, useEffect, useState } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { routes } from 'src/constants/routes';
import { UserDetailsPartial } from 'src/auth/types';
import { DetailsForm } from 'src/features/details-form';
import { userDataKeys } from 'src/features/details-form/constants';
import { UserDataFormValues } from 'src/features/details-form/types';
import { createUserDetailsSchema } from 'src/features/details-form/user-details-schema';
import { LoginDialog } from 'src/features/login-dialog';
import { useAddUserToBasket } from 'src/lib/hooks/use-post-add-user-to-basket';
import {
    checkSessionService,
    createUserAndLogInService,
    updateUserMetadataService,
} from 'src/services/auth0';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { BasketUser, setBasket, selectBasket, clearBasket } from 'src/store/slices/basket-slice';
import { selectBuilding, selectRoom } from 'src/store/slices/booking-slice';
import { setSnackbarState } from 'src/store/slices/snackbar-slice';
import {
    selectUserDetails,
    setDetails,
    selectUserLoggedIn,
    setTokenExpired,
} from 'src/store/slices/user-slice';
import { setBasketStorage, setUserStorage } from 'src/utils/local-storage-versioning';
import { validateEmail } from 'src/utils/validators/validate-email';
import { ValidationErrors } from 'src/shared/enum/ValidationErrors';
import { ErrorCode } from 'src/shared/enum/ErrorCode';
import { clearPaymentPlan } from 'src/store/slices/payment-plan-slice';
import { isUserAuthenticated } from 'src/auth/auth0';
import { useGetUserSpanishTaxInfo } from 'src/lib/hooks/use-get-user-spanish-tax-info';

import { EmailForm } from '../email-form';
import { useGetUserExists } from '../hooks/user';

import type { UserFormProps } from './types';

export const UserForm: FC<UserFormProps> = ({
    showDetailsForm,
    handleShowDetailsForm,
    handleHideDetailsForm,
    onFormSubmit,
    hasRoommates,
    handleUpdateNavigationStorage,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { data: userExists, fetchByEmail, isFetched, clearCache } = useGetUserExists();
    const { mutateAsync: addUserToBasket } = useAddUserToBasket();
    const userDetails = useSelector(selectUserDetails) as UserDetailsPartial;
    const building = useSelector(selectBuilding);
    const currentBasket = useSelector(selectBasket);
    const roomData = useSelector(selectRoom);
    const [isLoginDialogOpen, setIsLoginDialogOpen] = useState<boolean>(false);
    const [showPasswordReset, setShowPasswordReset] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const userLoggedIn = useSelector(selectUserLoggedIn);
    const { data: userSpanishTaxInfo } = useGetUserSpanishTaxInfo(userLoggedIn);
    const { t } = useTranslation();

    const toggleLoginDialog = () => {
        clearCache();
        setIsLoginDialogOpen((prev) => !prev);
        setShowPasswordReset(false);
    };

    const handleShowPasswordReset = () => {
        setShowPasswordReset(!showPasswordReset);
    };

    if (!building) throw new Error(ErrorMessage.NO_BOOKING_DETAILS_PROVIDED);

    const userDetailsSchema = createUserDetailsSchema(
        !userLoggedIn,
        building.spanish_tax_applicable
    );

    const methods = useForm<UserDataFormValues>({
        resolver: yupResolver(userDetailsSchema) as Resolver<UserDataFormValues>,
        defaultValues: {
            ...userDetails,
            nifNumber: userDetails?.nifNumber ?? userSpanishTaxInfo?.nif_number ?? null,
            nieNumber: userDetails?.nieNumber ?? userSpanishTaxInfo?.nie_number ?? null,
            passportNumber:
                userDetails?.passportNumber ?? userSpanishTaxInfo?.passport_number ?? null,
            passportIssuingCountry:
                userDetails?.passportIssuingCountry ??
                userSpanishTaxInfo?.passport_issuing_country ??
                null,
        },
        mode: 'all',
    });

    useEffect(() => {
        if (userSpanishTaxInfo) {
            const updatedDefaults = {
                ...methods.getValues(),
                nifNumber: userSpanishTaxInfo?.nif_number ?? null,
                nieNumber: userSpanishTaxInfo?.nie_number ?? null,
                passportNumber: userSpanishTaxInfo?.passport_number ?? null,
                passportIssuingCountry: userSpanishTaxInfo?.passport_issuing_country ?? null,
            };
            methods.reset(updatedDefaults);

            setDetails({
                ...userDetails,
                nifNumber: userSpanishTaxInfo?.nif_number ?? null,
                nieNumber: userSpanishTaxInfo?.nie_number ?? null,
                passportNumber: userSpanishTaxInfo?.passport_number ?? null,
                passportIssuingCountry: userSpanishTaxInfo?.passport_issuing_country ?? null,
            });
        }
    }, [userSpanishTaxInfo]);

    const { control, handleSubmit, watch, reset, getValues } = methods;

    const currentEmail = getValues(userDataKeys.email);

    useEffect(() => {
        if (isFetched && userExists && !userLoggedIn) {
            setIsLoginDialogOpen(true);
        }
        if (isFetched && !userExists) {
            handleShowDetailsForm();
        }
    }, [isFetched, userExists]);

    useEffect(() => {
        if (userLoggedIn) {
            reset({ ...userDetails });
            return;
        }
        handleHideDetailsForm();
    }, [userLoggedIn]);

    const handleContinue = () => {
        fetchByEmail(currentEmail);
    };

    const validateEmailField = () => {
        const fieldValue = watch(userDataKeys.email);
        return !!fieldValue && validateEmail(fieldValue);
    };

    const handleUpdate = async (user: UserDetailsPartial, basketUser: BasketUser) => {
        const sessionResult = await isUserAuthenticated();

        if (!sessionResult) {
            dispatch(setTokenExpired(true));
            navigate(routes.summary.index);
            return;
        }

        if (sessionResult && sessionResult.idTokenPayload && sessionResult.idTokenPayload.sub) {
            const userId = sessionResult.idTokenPayload.sub;
            await updateUserMetadataService(userId, user);
        }

        try {
            if (currentBasket && currentBasket.id && currentBasket.updated_date) {
                const response = await addUserToBasket({
                    basketId: currentBasket.id,
                    userData: basketUser,
                    updatedAt: currentBasket.updated_date,
                });
                dispatch(setBasket(response));
                setBasketStorage(response);
            }
        } catch (err) {
            if (err.response.data.code === ErrorCode.ERROR_BASKET_OUTDATED) {
                dispatch(clearBasket());
                dispatch(setSnackbarState({ message: t('basket_out_of_date_message') }));
                navigate(routes.summary.index);
                return;
            }
            if (err.response.data.code === ErrorCode.PROMOTION_EXPIRED) {
                dispatch(clearBasket());
                dispatch(clearPaymentPlan());
                dispatch(setSnackbarState({ message: t('promotion_expired_message') }));
                navigate(routes.summary.index);
                return;
            }
            if (err.response.data.code === ErrorCode.ALREADY_HAS_BOOKING) {
                dispatch(
                    setSnackbarState({
                        message: t('user_already_booked', {
                            academicYear: roomData?.academic_year,
                        }),
                    })
                );
                navigate(routes.summary.index);
                return;
            }
            if (err.response.data && err.response.data.data && err.response.data.data.room) {
                if (err.response.data.data.room[0] === ValidationErrors.ROOM_NOT_AVAILABLE) {
                    navigate(routes.roomNotAvailable.index);
                }
                if (err.response.data.data.room[0] === ValidationErrors.BASE_PRICE_MISMATCH) {
                    navigate(routes.summary.index);
                    dispatch(setSnackbarState({ message: t('room_price_updated_message') }));
                }
            }
        }
    };

    const onSubmit = handleSubmit(async (values) => {
        setLoading(true);

        const sessionResult = await checkSessionService();
        let userId: string | null = null;

        if (sessionResult && sessionResult.idTokenPayload && sessionResult.idTokenPayload.sub) {
            [, userId] = sessionResult.idTokenPayload.sub.split('|');
        }

        try {
            const dob = `${values.dateOfBirth.year}-${values.dateOfBirth.month?.padStart(2, '0')}-${values.dateOfBirth.day?.padStart(2, '0')}`;
            const phoneNumber = `+${values.phone.callingCode}${values.phone.number}`;
            const phoneCode = values.phone.countryCode || '';
            const nifNumber = values.nifNumber ?? null;
            const nieNumber = values.nieNumber ?? null;
            const passportNumber = values.passportNumber ?? null;
            const passportIssuingCountry = values.passportIssuingCountry ?? null;

            const user: UserDetailsPartial = {
                firstName: values.firstName,
                lastName: values.lastName,
                preferredName: values.preferredName,
                email: values.email,
                phone: values.phone,
                dateOfBirth: values.dateOfBirth,
                address1: values.address1,
                address2: values.address2,
                city: values.city,
                postCode: values.postCode,
                country: values.country,
                nationality: values.nationality,
                preferredLanguage: values.preferredLanguage,
                university: values.university,
                studyYear: values.studyYear,
                isSpanishResident: values.isSpanishResident,
                nifNumber: building.spanish_tax_applicable ? nifNumber : null,
                nieNumber: building.spanish_tax_applicable ? nieNumber : null,
                passportNumber: building.spanish_tax_applicable ? passportNumber : null,
                passportIssuingCountry: building.spanish_tax_applicable
                    ? passportIssuingCountry
                    : null,
                userFormConfirmation: values.userFormConfirmation,
            };
            const basketUser: BasketUser = {
                id: userId,
                first_name: values.firstName,
                last_name: values.lastName,
                address1: values.address1,
                address2: values.address2,
                city: values.city,
                country: values.country,
                nationality: values.nationality,
                number: phoneNumber,
                phone_code: phoneCode,
                post_code: values.postCode,
                study_year: values.studyYear,
                university: values.university,
                dob,
                email: values.email,
                preferred_language: values.preferredLanguage,
                is_spanish_resident: values.isSpanishResident === 'Yes',
                nif_number: building.spanish_tax_applicable ? nifNumber : null,
                nie_number: building.spanish_tax_applicable ? nieNumber : null,
                passport_number: building.spanish_tax_applicable ? passportNumber : null,
                passport_issuing_country: building.spanish_tax_applicable
                    ? passportIssuingCountry
                    : null,
            };

            setUserStorage(user);

            if (!userLoggedIn) {
                handleUpdateNavigationStorage();
                // user metadata processed in callback.tsx line 62
                await createUserAndLogInService(values.email, values.password, t);
            } else {
                await handleUpdate(user, basketUser);
            }

            await onFormSubmit();
        } catch (error) {
            const errorMessage = (error as Error).message;
            dispatch(setSnackbarState({ message: errorMessage }));
        } finally {
            setLoading(false);
        }
    });

    return (
        <FormProvider {...methods}>
            <Fragment>
                {!userLoggedIn && (
                    <EmailForm
                        control={control}
                        onContinue={handleContinue}
                        showAdditionalFields={showDetailsForm && !userExists}
                        showCta={!showDetailsForm}
                        isEmailValid={validateEmailField()}
                    />
                )}
                {(showDetailsForm || userLoggedIn) && (
                    <DetailsForm
                        control={control}
                        onFormSubmit={onSubmit}
                        hasRoommates={hasRoommates}
                        loading={loading}
                    />
                )}
                <LoginDialog
                    open={isLoginDialogOpen}
                    closeFn={toggleLoginDialog}
                    showPasswordReset={showPasswordReset}
                    handleShowPasswordReset={handleShowPasswordReset}
                    currentEmail={currentEmail}
                    redirectUrl={`${window.location.origin}${routes.callback.index}`}
                />
            </Fragment>
        </FormProvider>
    );
};
